// Parse the multilingual stops workbook.
// Expected shape (founder's sample: manchester_music_multilingual_stops.xlsx):
//   - One tab per language, named English / Spanish / German / French / Italian
//   - English tab: SL No. | Stop Name | Description | Lattitude | Longitude | Radius
//     (+ optional: Audio Lock | Hide Stop)
//   - Other tabs:  SL No. | Stop Name | Description
// Strictness rules (from the adversarial verification):
//   - SL No must be unique, contiguous 1..N, identical across every tab
//   - coords/radius must be dot-decimal numbers; comma decimals are REJECTED,
//     never coerced (parseFloat("53,48") silently truncates to 53)
//   - formula cells are read via their computed values (xlsx .v)

import * as XLSX from "xlsx";
import { TAB_LANG, LIMITS } from "./constants";
import { blocker, warning, info } from "./issues";

function normHeader(h) {
  return String(h ?? "").toLowerCase().replace(/[^a-z]/g, "");
}

const HEADER_MAP = {
  slno: "sl",
  sl: "sl",
  stopname: "name",
  name: "name",
  description: "description",
  lattitude: "lat", // the sheet mirrors the DB's misspelling
  latitude: "lat",
  longitude: "lng",
  radius: "radius",
  audiolock: "audioLock",
  lockaudio: "audioLock",
  locklanguageaudio: "audioLock",
  hidestop: "hideStop",
};

// Strict dot-decimal number. Returns null and records an issue on anything else.
function strictNumber(value, field, sl, tab, issues) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const s = value.trim();
    if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);
    const commaHint = s.includes(",")
      ? " (comma decimals are not accepted — use a dot: 53.478, not 53,478)"
      : "";
    issues.push(
      blocker("bad-number", `[${tab}] stop ${sl}: ${field} "${s}" is not a plain decimal number${commaHint}`, { sl })
    );
    return null;
  }
  issues.push(blocker("missing-number", `[${tab}] stop ${sl}: ${field} is empty`, { sl }));
  return null;
}

function parseBoolCell(value, field, sl, tab, issues) {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const s = String(value).trim().toLowerCase();
  if (["locked", "lock", "yes", "y", "true", "1"].includes(s)) return true;
  if (["free", "unlocked", "no", "n", "false", "0"].includes(s)) return false;
  issues.push(blocker("bad-flag", `[${tab}] stop ${sl}: ${field} "${value}" — use locked/free or yes/no`, { sl }));
  return null;
}

/**
 * @param {string|Uint8Array|Buffer} source  path to .xlsx (Node) or the file bytes
 * @returns {{languages: string[], stops: object[], issues: object[]}}
 */
export function parseSheet(source) {
  const issues = [];
  // string = file path (Node CLI); anything else = bytes (Buffer/Uint8Array/ArrayBuffer — browser-safe)
  const wb = typeof source === "string" ? XLSX.readFile(source) : XLSX.read(source, { type: "array" });

  // Map recognized tabs to language codes
  const tabs = [];
  for (const sheetName of wb.SheetNames) {
    const code = TAB_LANG[sheetName.trim().toLowerCase()];
    if (code) tabs.push({ sheetName, code });
    else if (!/^_/.test(sheetName)) issues.push(info("ignored-tab", `Tab "${sheetName}" not recognized as a language — ignored`));
  }
  if (!tabs.find((t) => t.code === "en")) {
    issues.push(blocker("no-english-tab", "No English tab found — the English tab carries coordinates and is required"));
    return { languages: [], stops: [], issues };
  }
  const dupCodes = tabs.map((t) => t.code).filter((c, i, a) => a.indexOf(c) !== i);
  for (const c of new Set(dupCodes)) issues.push(blocker("dup-language-tab", `More than one tab maps to language "${c}"`));

  // Parse each tab into rows keyed by normalized headers
  const perTab = {};
  for (const { sheetName, code } of tabs) {
    const ws = wb.Sheets[sheetName];
    const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null });
    // Header row = first row that maps BOTH an SL column and a Stop Name column
    // (a single 'sl'-ish cell is not enough — title rows above the header would hijack it).
    const mapsOf = (row) => (row || []).map((h) => HEADER_MAP[normHeader(h)] || null);
    let headerIdx = grid.findIndex((row) => {
      const m = mapsOf(row);
      return m.includes("sl") && m.includes("name");
    });
    if (headerIdx === -1) {
      issues.push(blocker("no-header", `[${sheetName}] no header row with both "SL No." and "Stop Name" columns found`));
      continue;
    }
    const cols = mapsOf(grid[headerIdx]);
    // Two source columns mapping to the same field (e.g. "Latitude" + "Lattitude") would
    // silently let the last one win — make it explicit instead.
    const seenKeys = new Set();
    cols.forEach((key, i) => {
      if (!key) return;
      if (seenKeys.has(key)) issues.push(blocker("dup-header", `[${sheetName}] two columns map to "${key}" — remove one`));
      seenKeys.add(key);
    });
    const rows = [];
    for (let r = headerIdx + 1; r < grid.length; r++) {
      const raw = grid[r] || [];
      if (raw.every((c) => c === null || String(c).trim() === "")) continue; // trailing empties
      const row = {};
      cols.forEach((key, i) => { if (key) row[key] = raw[i]; });
      row.__rownum = r + 1;
      rows.push(row);
    }
    perTab[code] = { sheetName, cols, rows };
  }

  // Validate SL sets per tab: unique, contiguous 1..N
  const slSets = {};
  for (const [code, tab] of Object.entries(perTab)) {
    const sls = [];
    for (const row of tab.rows) {
      const n = Number(row.sl);
      if (!Number.isInteger(n) || n < 1) {
        issues.push(blocker("bad-sl", `[${tab.sheetName}] row ${row.__rownum}: SL No. "${row.sl}" is not a positive integer`));
        continue;
      }
      if (sls.includes(n)) issues.push(blocker("dup-sl", `[${tab.sheetName}] duplicate SL No. ${n}`, { sl: n }));
      sls.push(n);
      row.sl = n;
    }
    slSets[code] = sls;
    const sorted = [...new Set(sls)].sort((a, b) => a - b);
    const contiguous = sorted.every((v, i) => v === i + 1);
    if (!contiguous) {
      issues.push(blocker("sl-not-contiguous", `[${tab.sheetName}] SL numbers must run 1..N without gaps (got: ${sorted.join(",")})`));
    }
  }

  // All tabs must cover the same SL set as English
  const enSet = new Set(slSets.en || []);
  for (const [code, sls] of Object.entries(slSets)) {
    if (code === "en") continue;
    const tabName = perTab[code].sheetName;
    for (const n of enSet) if (!sls.includes(n)) issues.push(blocker("missing-translation", `[${tabName}] stop ${n} has no row (missing translation)`, { sl: n, lang: code }));
    for (const n of sls) if (!enSet.has(n)) issues.push(blocker("extra-row", `[${tabName}] stop ${n} does not exist on the English tab`, { sl: n, lang: code }));
  }

  // Build the stop model from the English tab, merge translations
  const en = perTab.en;
  const enHasLockCol = en ? en.cols.includes("audioLock") : false;
  const enHasHideCol = en ? en.cols.includes("hideStop") : false;
  const stops = [];
  if (en) {
    const byNum = new Map(en.rows.map((r) => [r.sl, r]));
    const ordered = [...byNum.keys()].filter(Number.isInteger).sort((a, b) => a - b);
    for (const sl of ordered) {
      const row = byNum.get(sl);
      const stop = {
        sl,
        names: {},
        descriptions: {},
        lat: strictNumber(row.lat, "Latitude", sl, en.sheetName, issues),
        lng: strictNumber(row.lng, "Longitude", sl, en.sheetName, issues),
        radius: strictNumber(row.radius, "Radius", sl, en.sheetName, issues),
        audioLock: enHasLockCol ? parseBoolCell(row.audioLock, "Audio Lock", sl, en.sheetName, issues) : null,
        hideStop: enHasHideCol ? (parseBoolCell(row.hideStop, "Hide Stop", sl, en.sheetName, issues) ?? false) : false,
        image: null,
        audio: {},
      };
      for (const [code, tab] of Object.entries(perTab)) {
        const tRow = tab.rows.find((r) => r.sl === sl);
        if (!tRow) continue;
        const name = String(tRow.name ?? "").trim();
        const desc = String(tRow.description ?? "").trim();
        if (!name) issues.push(blocker("empty-name", `[${tab.sheetName}] stop ${sl}: Stop Name is empty`, { sl, lang: code }));
        if (!desc) issues.push(blocker("empty-description", `[${tab.sheetName}] stop ${sl}: Description is empty`, { sl, lang: code }));
        stop.names[code] = name;
        stop.descriptions[code] = desc;
      }
      // coordinate bounds
      if (stop.lat !== null && (stop.lat < -90 || stop.lat > 90)) issues.push(blocker("lat-range", `stop ${sl}: latitude ${stop.lat} out of range`, { sl }));
      if (stop.lng !== null && (stop.lng < -180 || stop.lng > 180)) issues.push(blocker("lng-range", `stop ${sl}: longitude ${stop.lng} out of range`, { sl }));
      if (stop.radius !== null) {
        if (stop.radius <= 0) issues.push(blocker("radius-invalid", `stop ${sl}: radius ${stop.radius} must be > 0`, { sl }));
        else if (stop.radius > LIMITS.radiusHardMax) {
          issues.push(blocker("radius-absurd", `stop ${sl}: radius ${stop.radius}m is over ${LIMITS.radiusHardMax}m — almost certainly a typo or an Excel date leak`, { sl }));
        } else if (stop.radius < LIMITS.radiusMin || stop.radius > LIMITS.radiusMax) {
          issues.push(warning("radius-unusual", `stop ${sl}: radius ${stop.radius}m outside the usual ${LIMITS.radiusMin}-${LIMITS.radiusMax}m`, { sl }));
        }
      }
      stops.push(stop);
    }
  }

  // Audio Lock default: column absent (or every cell empty) -> first N free
  const anyLockSet = stops.some((s) => s.audioLock !== null);
  if (!anyLockSet) {
    for (const s of stops) s.audioLock = s.sl > LIMITS.freeStopsDefault;
    if (stops.length) issues.push(info("lock-default", `No Audio Lock column — applied default: first ${LIMITS.freeStopsDefault} stops free, rest locked`));
  } else {
    for (const s of stops) {
      if (s.audioLock === null) issues.push(blocker("lock-missing", `stop ${s.sl}: Audio Lock column exists but this cell is empty — fill locked/free`, { sl: s.sl }));
    }
  }

  // Duplicate stop identity (same EN name + ~same location) would break the
  // engine's resume reconciliation — block it outright.
  for (let i = 0; i < stops.length; i++) {
    for (let j = i + 1; j < stops.length; j++) {
      const a = stops[i], b = stops[j];
      if (a.names.en && a.names.en === b.names.en && a.lat !== null && b.lat !== null && Math.abs(a.lat - b.lat) < 0.001) {
        issues.push(blocker("dup-stop-identity", `stops ${a.sl} and ${b.sl} share the same English name and location — give them distinct names (resume matching needs unique identities)`, { sl: b.sl }));
      }
    }
  }

  // Proximity sanity: every stop within LIMITS.proximityKm of the centroid
  const located = stops.filter((s) => s.lat !== null && s.lng !== null);
  if (located.length > 1) {
    const cLat = located.reduce((a, s) => a + s.lat, 0) / located.length;
    const cLng = located.reduce((a, s) => a + s.lng, 0) / located.length;
    for (const s of located) {
      const km = haversineKm(cLat, cLng, s.lat, s.lng);
      if (km > LIMITS.proximityKm) issues.push(warning("far-from-centroid", `stop ${s.sl} is ${km.toFixed(1)} km from the tour centroid — check the coordinates`, { sl: s.sl }));
    }
  }

  if (stops.length < LIMITS.minStops) {
    issues.push(blocker("too-few-stops", `Only ${stops.length} stop(s) — route generation requires at least ${LIMITS.minStops}`));
  }

  const languages = tabs.map((t) => t.code);
  return { languages, stops, issues };
}

export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}


